import { nanoid } from '@reduxjs/toolkit'
import { orderBy } from 'lodash'
import type { FreelancerBillingByProjectFreelancerQuery } from 'src/API'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import type { UserInfo } from 'src/models'
import { graphqlApi } from 'src/redux/api/graphql/graphqlApi'
import type {
	GetProjectTotalsQueryApi,
	ReduxProjects,
	ReduxQuote,
	ReduxUserInfo,
} from 'src/types/apiType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { makeErrorToast, makeSuccessToast, msg } from 'src/utils/makeToast'
import { captureExceptionAndThrow } from 'src/utils/posthogException'
import { getProperUpdateValues } from 'src/utils/updateApiUtil'

export const projectApi = graphqlApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchFreelancerProjects: builder.query({
			query: ({
				currentCompanyTenantId,
				freelancerTenantId,
				sort,
			}: {
				currentCompanyTenantId: string
				freelancerTenantId: string
				sort: string
			}) => ({
				queryName: 'projectsByTenantId',
				data: {
					tenantId: currentCompanyTenantId,
					filter: {
						activeFlag: { eq: true },
						freelancers: { contains: freelancerTenantId },
					},
					limit: 1000,
				},
				isList: true,
			}),
			transformResponse: (response: ReduxProjects[], meta, arg) => {
				let sortedProjects: ReduxProjects[] = []
				if (arg.sort === 'date') {
					sortedProjects = orderBy(response, ['createdOn'], ['asc'])
				} else if (arg.sort === 'dateM') {
					sortedProjects = orderBy(response, ['createdOn'], ['desc'])
				} else if (arg.sort === 'name') {
					sortedProjects = orderBy(
						response,
						[(project) => project.name?.toLowerCase()],
						['asc']
					)
				}
				return sortedProjects
			},
			providesTags: (result) =>
				result
					? [
							...result.map((r: ReduxProjects) => ({
								type: 'Projects' as const,
								id: r.id as string,
							})),
							{ type: 'Projects', id: 'LIST' },
						]
					: [{ type: 'Projects', id: 'LIST' }],
		}),
		fetchSubTeamAdminProjects: builder.query({
			query: ({
				currentCompanyTenantId,
				userInfo,
				sort,
			}: {
				currentCompanyTenantId: string
				userInfo: UserInfo
				sort: string
			}) => ({
				queryName: 'projectsByTenantId',
				data: {
					tenantId: currentCompanyTenantId,
					filter: {
						activeFlag: { eq: true },
						parentClientId: { attributeExists: true },
					},
					limit: 1000,
				},
				isList: true,
			}),
			transformResponse: (response: ReduxProjects[], meta, arg) => {
				const filteredProjects = response.filter((project) => {
					return arg.userInfo.subTeamIds?.includes(project.id)
				})
				let sortedProjects: ReduxProjects[] = []
				if (arg.sort === 'date') {
					sortedProjects = orderBy(
						filteredProjects,
						['createdOn'],
						['asc']
					)
				}
				if (arg.sort === 'dateM') {
					sortedProjects = orderBy(
						filteredProjects,
						['createdOn'],
						['desc']
					)
				}
				if (arg.sort === 'name') {
					sortedProjects = orderBy(
						filteredProjects,
						[(project) => project.name?.toLowerCase()],
						['asc']
					)
				}
				return sortedProjects
			},
			providesTags: (result) =>
				result
					? [
							...result.map((r: ReduxProjects) => ({
								type: 'Projects' as const,
								id: r.id as string,
							})),
							{ type: 'Projects', id: 'LIST' },
						]
					: [{ type: 'Projects', id: 'LIST' }],
		}),
		fetchChildFreelancerProjects: builder.query({
			query: ({
				currentCompanyTenantId,
				freelancerTenantId,
			}: {
				currentCompanyTenantId: string
				freelancerTenantId: string
			}) => ({
				queryName: 'projectsByTenantId',
				data: {
					tenantId: currentCompanyTenantId,
					filter: {
						activeFlag: { eq: true },
						childFreelancers: { contains: freelancerTenantId },
					},
					limit: 1000,
				},
				isList: true,
			}),
			transformResponse: (response: ReduxProjects[]) => {
				return response
			},
			providesTags: (result) =>
				result
					? [
							...result.map((r: ReduxProjects) => ({
								type: 'Projects' as const,
								id: r.id as string,
							})),
							{ type: 'Projects', id: 'LIST' },
						]
					: [{ type: 'Projects', id: 'LIST' }],
		}),
		fetchProjectsById: builder.query({
			query: (id: string) => ({
				queryName: 'getProjects',
				data: {
					id,
				},
				isList: false,
			}),
			transformResponse: (response: ReduxProjects) => {
				const typedResponse = response
				return typedResponse
			},
			providesTags: (result, error, id) => [{ type: 'Projects', id }],
		}),
		fetchProjectsByTenantId: builder.query({
			query: ({
				tenantId,
				filter,
			}: {
				tenantId: string
				filter: any
			}) => ({
				queryName: 'projectsByTenantIdBasic',
				apiName: 'projectsByTenantId',
				data: {
					tenantId,
					filter,
					limit: 1000,
				},
				isList: true,
			}),
			transformResponse: (response: ReduxProjects[]) => response,
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }) => ({
								type: 'Projects' as const,
								id: id as string,
							})),
							{ type: 'Projects', id: 'LIST' },
						]
					: [{ type: 'Projects', id: 'LIST' }],
		}),
		fetchProjectsByParentClientId: builder.query({
			serializeQueryArgs: ({ queryArgs }) => {
				try {
					return `projectsByClient:${(queryArgs as any)?.parentClientId ?? ''}`
				} catch {
					return 'projectsByClient:'
				}
			},
			forceRefetch({ currentArg, previousArg }) {
				if (!currentArg || !previousArg) return true
				return (
					currentArg.parentClientId !== previousArg.parentClientId ||
					currentArg.sort !== previousArg.sort ||
					JSON.stringify(currentArg.filter ?? {}) !==
						JSON.stringify(previousArg.filter ?? {})
				)
			},
			keepUnusedDataFor: 300,
			query: ({
				parentClientId,
				filter,
				sort,
			}: {
				parentClientId: string
				filter: any
				sort: string
			}) => ({
				queryName: 'projectsByClientId',
				apiName: 'projectsByClientId',
				data: {
					parentClientId,
					filter,
					limit: 1000,
				},
				isList: true,
			}),
			transformResponse: (response: ReduxProjects[], meta, arg) => {
				let sortedProjects: ReduxProjects[] = []
				if (arg.sort === 'date') {
					sortedProjects = orderBy(response, ['createdOn'], ['asc'])
				} else if (arg.sort === 'dateM') {
					sortedProjects = orderBy(response, ['createdOn'], ['desc'])
				} else if (arg.sort === 'name') {
					sortedProjects = orderBy(
						response,
						[(project) => project.name?.toLowerCase()],
						['asc']
					)
				}
				return sortedProjects
			},
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }: { id: string }) => ({
								type: 'Projects' as const,
								id: id as string,
							})),
							{ type: 'Projects', id: 'LIST' },
						]
					: [{ type: 'Projects', id: 'LIST' }],
		}),
		fetchProjectTotalsById: builder.query({
			query: (projectId: string) => ({
				queryName: 'getProjectTotals',
				data: {
					projectId,
				},
				isList: false,
			}),
			transformResponse: (
				response: GetProjectTotalsQueryApi['getProjectTotals']
			) => {
				const typedResponse = response
				return typedResponse
			},
			async onQueryStarted(projectId, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled
					if (data?.projectTypeAutoCorrected) {
						dispatch(
							projectApi.util.invalidateTags([
								{ type: 'Projects', id: projectId },
								{ type: 'Projects', id: 'LIST' },
							])
						)
					}
				} catch {
					// Errors are handled by the caller path.
				}
			},
			providesTags: (result, error, projectId) => [
				{ type: 'ProjectTotals', id: projectId },
			],
		}),
		fetchProjectChildren: builder.query({
			// Share cache by parentProjectId so previously fetched data
			// shows immediately when args (e.g., sort/filter) change.
			serializeQueryArgs: ({ queryArgs }) => {
				try {
					return `projectChildren:${(queryArgs as any)?.parentProjectId ?? ''}`
				} catch {
					return 'projectChildren:'
				}
			},
			// Always refetch when sort or filter changes, but keep cached data for instant UI.
			forceRefetch({ currentArg, previousArg }) {
				if (!currentArg || !previousArg) return true
				return (
					currentArg.parentProjectId !==
						previousArg.parentProjectId ||
					currentArg.sort !== previousArg.sort ||
					JSON.stringify(currentArg.filter ?? {}) !==
						JSON.stringify(previousArg.filter ?? {})
				)
			},
			keepUnusedDataFor: 300,
			queryFn: async ({
				parentProjectId,
				filter,
				sort,
			}: {
				parentProjectId: string
				filter: any
				sort: string
			}) => {
				try {
					// Fetch projects by parent project ID
					const {
						data: {
							projectsByParentProjectId: { items: projectItems },
						},
					} = (await API.graphql(
						graphqlOperation(queries.projectsByParentProjectId, {
							parentProjectId,
							filter,
							limit: 1000,
						})
					)) as {
						data: {
							projectsByParentProjectId: {
								items: ReduxProjects[]
							}
						}
					}

					// Fetch quotes by parent project ID
					const {
						data: {
							quotesByParentProjectId: { items: quoteItems },
						},
					} = (await API.graphql(
						graphqlOperation(queries.quotesByParentProjectId, {
							parentProjectId,
							filter,
							limit: 1000,
						})
					)) as {
						data: {
							quotesByParentProjectId: { items: ReduxQuote[] }
						}
					}

					// Combine project and quote data
					const projectChildrenData = [...projectItems, ...quoteItems]

					// Sort the combined data
					let sortedProjectChildren = projectChildrenData
					if (sort === 'date') {
						sortedProjectChildren = orderBy(
							projectChildrenData,
							['createdOn'],
							['asc']
						)
					} else if (sort === 'dateM') {
						sortedProjectChildren = orderBy(
							projectChildrenData,
							['createdOn'],
							['desc']
						)
					} else if (sort === 'name') {
						sortedProjectChildren = orderBy(
							projectChildrenData,
							[
								(projectChild) =>
									projectChild?.name?.toLowerCase(),
							],
							['asc']
						)
					}

					return { data: sortedProjectChildren }
				} catch (error: any) {
					console.log('err fetchProjectChildren: ', error)
					makeErrorToast(msg)
					return { error }
				}
			},
			providesTags: (result, _error, { parentProjectId }) => [
				{ type: 'Projects', id: `${parentProjectId}-children` },
				...((result || []) as (ReduxProjects | ReduxQuote)[]).map(
					(item) => {
						if (item.__typename === 'Projects') {
							return {
								type: 'Projects' as const,
								id: item.id as string,
							}
						}
						return {
							type: 'Quote' as const,
							id: item.id as string,
						}
					}
				),
			],
		}),
		fetchProject: builder.query({
			serializeQueryArgs: ({ queryArgs }) => {
				try {
					return `projectsByClient:${(queryArgs as any)?.parentClientId ?? ''}`
				} catch {
					return 'projectsByClient:'
				}
			},
			forceRefetch({ currentArg, previousArg }) {
				if (!currentArg || !previousArg) return true
				return (
					currentArg.parentClientId !== previousArg.parentClientId ||
					currentArg.sort !== previousArg.sort ||
					JSON.stringify(currentArg.filter ?? {}) !==
						JSON.stringify(previousArg.filter ?? {})
				)
			},
			keepUnusedDataFor: 300,
			queryFn: async ({
				parentClientId,
				filter,
				sort,
			}: {
				parentClientId: string
				filter: any
				sort: string
			}) => {
				try {
					// Fetch projects by client ID
					const {
						data: {
							projectsByClientId: { items },
						},
					} = (await API.graphql(
						graphqlOperation(queries.projectsByClientId, {
							parentClientId,
							filter,
							limit: 1000,
						})
					)) as {
						data: {
							projectsByClientId: { items: ReduxProjects[] }
						}
					}

					// Sort the projects
					let sortedProjects: ReduxProjects[] = []
					if (sort === 'date') {
						sortedProjects = orderBy(items, ['createdOn'], ['asc'])
					} else if (sort === 'dateM') {
						sortedProjects = orderBy(items, ['createdOn'], ['desc'])
					} else if (sort === 'name') {
						sortedProjects = orderBy(
							items,
							[(project) => project?.name?.toLowerCase()],
							['asc']
						)
					} else {
						sortedProjects = items
					}

					return { data: sortedProjects }
				} catch (error: any) {
					console.log('err fetchProject: ', error)
					makeErrorToast(msg)
					return { error }
				}
			},
			providesTags: (result, _error, { parentClientId }) => [
				{ type: 'Projects', id: `${parentClientId}-projects` },
				...((result || []) as ReduxProjects[]).map((project) => ({
					type: 'Projects' as const,
					id: project.id as string,
				})),
			],
		}),
		createSubTeamById: builder.mutation({
			queryFn: async ({
				subTeamName,
				tenantId,
				subTeamCount,
				parentClientId,
			}: {
				subTeamName: string
				tenantId: string
				subTeamCount: string
				parentClientId: string
			}) => {
				try {
					const id = nanoid()
					const newSubTeam = {
						id,
						name: subTeamName,
						tenantId: tenantId,
						subTeamCount: `${subTeamName}-${subTeamCount}`,
						parentClientId: parentClientId,
						parentTeamId: parentClientId,
						parentSubTeamId: id,
						type: 'project',
						isSubTeam: true,
						activeFlag: true,
						offlineIds: ['dummyId'],
						childFreelancers: ['dummyId'],
					} as ReduxProjects

					await API.graphql(
						graphqlOperation(mutations.createProjects, {
							input: newSubTeam,
						})
					)
					return { data: null }
				} catch (error) {
					makeErrorToast(msg)
					return { error }
				}
			},
			invalidatesTags: () => [{ type: 'Projects', id: 'LIST' }],
		}),
		updateSubTeamById: builder.mutation({
			queryFn: async ({
				project,
				subTeamName,
				subTeamCount,
			}: {
				project: ReduxProjects
				subTeamName: string
				subTeamCount: string
			}) => {
				try {
					const {
						data: { getProjects },
					} = (await API.graphql(
						graphqlOperation(queries.getProjects, {
							id: project.id,
						})
					)) as {
						data: { getProjects: ReduxProjects }
					}
					const updatedProject = getProperUpdateValues<ReduxProjects>(
						{
							updatedInput: {
								name: subTeamName,
								subTeamCount: subTeamCount,
							},
							originalInput: getProjects,
						}
					)
					await API.graphql(
						graphqlOperation(mutations.updateProjects, {
							input: updatedProject,
						})
					)
					return { data: null }
				} catch (error: any) {
					makeErrorToast(msg)
					return { error }
				}
			},
			invalidatesTags: () => [{ type: 'Projects', id: 'LIST' }],
		}),
		fetchFreelancerInfoByProject: builder.query({
			queryFn: async (projectId: string) => {
				try {
					// First get the project to access freelancers array
					const {
						data: { getProjects: project },
					} = (await API.graphql(
						graphqlOperation(queries.getProjects, {
							id: projectId,
						})
					)) as {
						data: { getProjects: ReduxProjects }
					}

					const freelancers = project?.freelancers ?? []
					// Since we are adding dummyId for all freelancers we need to
					//  remove that from freelancers when searching for email
					const removeDummyIdFromFreelancersArray =
						freelancers.filter((i) => i !== 'dummyId')
					const freelanceEmail = [] as ReduxUserInfo[]

					// Only check further if freelancers array has other elements other than dummyId
					if (removeDummyIdFromFreelancersArray.length === 0) {
						return { data: freelanceEmail }
					}

					await Promise.all(
						removeDummyIdFromFreelancersArray.map(
							async (freelancersId) => {
								const {
									data: {
										userInfoByTenantId: {
											items: freelancerInfoArray,
										},
									},
								} = (await API.graphql(
									graphqlOperation(
										queries.userInfoByTenantId,
										{
											tenantId: freelancersId,
										}
									)
								)) as {
									data: {
										userInfoByTenantId: {
											items: ReduxUserInfo[]
										}
									}
								}
								// Telling typescript we have the particular nested object items
								if (
									Array.isArray(freelancerInfoArray) &&
									freelancerInfoArray?.length === 1
								) {
									freelanceEmail.push(freelancerInfoArray[0])
								}
							}
						)
					)
					return { data: freelanceEmail }
				} catch (error: any) {
					console.log('err fetchFreelancerInfoByProject: ', error)
					makeErrorToast(msg)
					return { error }
				}
			},
			providesTags: (result, error, projectId) => [
				{ type: 'Projects', id: `${projectId}-freelancers` },
			],
		}),
		checkFreelancerBillingStatus: builder.query({
			queryFn: async ({
				projectId,
				freelancerId,
			}: {
				projectId: string
				freelancerId: string
			}) => {
				try {
					const projectFreelancerKey = `${projectId}#${freelancerId}`

					const {
						data: {
							freelancerBillingByProjectFreelancer: billingData,
						},
					} = (await API.graphql(
						graphqlOperation(
							queries.freelancerBillingByProjectFreelancer,
							{
								projectFreelancerKey,
								filter: {
									activeFlag: { eq: true },
								},
							}
						)
					)) as {
						data: FreelancerBillingByProjectFreelancerQuery
					}

					const billingRecords = billingData?.items || []

					// Check if there's a CHARGED billing record
					const chargedRecord = billingRecords.find(
						(record) =>
							record?.status === 'CHARGED' ||
							record?.status === 'CHARGE_PENDING'
					)

					return {
						data: {
							hasChargedRecord: !!chargedRecord,
							chargedRecord,
							billingRecords,
							projectFreelancerKey,
						},
					}
				} catch (error: any) {
					console.log('err checkFreelancerBillingStatus: ', error)
					return { error }
				}
			},
			providesTags: (
				result: any,
				error: any,
				{
					projectId,
					freelancerId,
				}: {
					projectId: string
					freelancerId: string
				}
			) => [
				{
					type: 'FreelancerBilling',
					id: `${projectId}-${freelancerId}`,
				},
			],
		}),
		createFreelancerBillingRecord: builder.mutation({
			queryFn: async ({
				projectId,
				freelancerId,
				tenantId,
			}: {
				projectId: string
				freelancerId: string
				tenantId: string
			}) => {
				try {
					const result = (await API.graphql(
						graphqlOperation(
							mutations.createFreelanceBillingRecord,
							{
								projectId,
								freelancerId,
								tenantId,
							}
						)
					)) as {
						data: {
							createFreelanceBillingRecord: {
								status: string
								message: string
							}
						}
					}

					return { data: result.data.createFreelanceBillingRecord }
				} catch (error) {
					console.log('err chargeFreelancer: ', { error })
					return { error }
				}
			},
			invalidatesTags: (result, error, { projectId, freelancerId }) => [
				{
					type: 'FreelancerBilling',
					id: `${projectId}-${freelancerId}`,
				},
				{ type: 'Projects', id: projectId },
			],
		}),
		refundFreelancer: builder.mutation({
			queryFn: async ({
				projectId,
				freelancerId,
				tenantId,
			}: {
				projectId: string
				freelancerId: string
				tenantId: string
			}) => {
				try {
					const result = (await API.graphql(
						graphqlOperation(mutations.refundFreelancer, {
							projectId,
							freelancerId,
							tenantId,
						})
					)) as {
						data: {
							refundFreelancer: {
								status: string
								message: string
							}
						}
					}

					return { data: result.data.refundFreelancer }
				} catch (error: any) {
					console.log('err refundFreelancer: ', error)
					return { error }
				}
			},
			invalidatesTags: (result, error, { projectId, freelancerId }) => [
				{
					type: 'FreelancerBilling',
					id: `${projectId}-${freelancerId}`,
				},
				{ type: 'Projects', id: `${projectId}-freelancers` },
			],
		}),
		addFreelancerToProject: builder.mutation({
			queryFn: async ({
				projectId,
				freelancerId,
				tenantId,
			}: {
				projectId: string
				freelancerId: string
				tenantId: string
			}) => {
				// !* Only to be done after charge freelancer checks are done
				try {
					const result = (await API.graphql(
						graphqlOperation(mutations.addFreelancerToProject, {
							projectId,
							freelancerId,
						})
					)) as {
						data: {
							addFreelancerToProject: {
								status: string
								message: string
							}
						}
					}
					// freelancerId here is the tenantId used in projects.freelancers
					// Fetch the actual user by tenantId to update their projectIds/companyTenantId
					const {
						data: {
							userInfoByTenantId: { items: usersByTenant },
						},
					} = (await API.graphql(
						graphqlOperation(queries.userInfoByTenantId, {
							tenantId: freelancerId,
						})
					)) as {
						data: { userInfoByTenantId: { items: ReduxUserInfo[] } }
					}

					const latestUserInfo = usersByTenant?.[0]
					if (!latestUserInfo) {
						captureExceptionAndThrow(
							new Error('User not found for tenantId'),
							{
								location: 'projectsApi.addFreelancerToProject',
								projectId,
								freelancerTenantId: freelancerId,
								tenantId,
							}
						)
					}

					const updateInput: Partial<ReduxUserInfo> = {
						id: latestUserInfo.id,
						projectIds: [
							...new Set([
								...(latestUserInfo.projectIds || []),
								projectId,
							]),
						],
						companyTenantId: [
							...new Set([
								...(latestUserInfo.companyTenantId || []),
								tenantId,
							]),
						],
					}
					await API.graphql(
						graphqlOperation(mutations.updateUserInfo, {
							input: updateInput,
						})
					)
					makeSuccessToast('Freelancer added to project')

					return { data: result.data.addFreelancerToProject }
				} catch (error: any) {
					console.log('err addFreelancerToProject: ', error)
					return { error }
				}
			},
			invalidatesTags: (result, error, { projectId, freelancerId }) => [
				{ type: 'Projects', id: projectId },
			],
		}),
	}),
	overrideExisting: false,
})

export const {
	useFetchProjectsByIdQuery,
	useFetchSubTeamAdminProjectsQuery,
	useFetchProjectsByTenantIdQuery,
	useFetchChildFreelancerProjectsQuery,
	useFetchFreelancerProjectsQuery,
	useFetchProjectTotalsByIdQuery,
	useFetchProjectsByParentClientIdQuery,
	useFetchProjectChildrenQuery,
	useFetchProjectQuery,
	useCreateSubTeamByIdMutation,
	useUpdateSubTeamByIdMutation,
	useFetchFreelancerInfoByProjectQuery,
	useCheckFreelancerBillingStatusQuery,
	useCreateFreelancerBillingRecordMutation,
	useRefundFreelancerMutation,
	useAddFreelancerToProjectMutation,
} = projectApi
